'use strict';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {getOrganization, getProject} from '../../app/model/reducers/state';
import {AuthCacheService} from '../../auth/services/AuthCacheService';
import {CLIENT_VERSION} from '../../config/constants';


const CACHE_ID = "AppSettingsService-settings-data";

// TODO(i18n)
// TODO: labels are misleading since the camera aspect ratio is going to cleave 1/4-1/2 off the size
const cameraResolutions = [
  {value: 1280, label: '1.3 Megapixels', resolution: '1280x1024'},
  {value: 1600, label: '2 Megapixels', resolution: '1600x1200'},
  {value: 2240, label: '4 Megapixels', resolution: '2240x1680'},
  {value: 2816, label: '6 Megapixels', resolution: '2816x2112'},
  {value: 3264, label: '8 Megapixels', resolution: '3264x2448'},
  {value: 0, label: 'Max Supported'},
];

const vidQualities = [
  {value: 0, label: 'Standard Quality'},
  {value: 1, label: 'High Quality'},
];

export function $settingsProvider() {
  return {
    $get: [
      AuthCacheService.SERVICE_NAME,
      '$ngRedux',
      '$persistentCacheFactory',
      provider,
    ],
  };

  function provider(authCacheService, $ngRedux, $persistentCacheFactory) {
    // TODO: migrate existing settings from $persistentCacheFactory to authCacheService
    return new AppSettings(
      authCacheService,
      $ngRedux,
      $persistentCacheFactory.get(CACHE_ID) || $persistentCacheFactory(CACHE_ID),
    );
  }
}

$settingsProvider.SERVICE_NAME = '$settings';

function AppSettings(
  authCacheService,
  $ngRedux,
  cache,
) {
  const audioN10nVolume$ = new ReplaySubject(1);
  this.audioN10nVolume$ = audioN10nVolume$.asObservable();

  let autoRetryUploads;
  let enablePushNotifications;
  let allowCameraEditing;
  let jpgQuality;
  let cameraResolution;
  let correctOrientation;
  let vidQuality;
  let copyToGallery;
  let useAudioN10ns;
  let audioN10nVolume;

  migrateExistingCache(cache, authCacheService)
    .then(() =>
      Promise.props({
        autoRetryUploads: authCacheService.getItem('autoRetryUploads'),
        enablePushNotifications: authCacheService.getItem('enablePushNotifications'),
        allowCameraEditing: authCacheService.getItem('allowCameraEditing'),
        jpgQuality: authCacheService.getItem('jpgQuality'),
        cameraResolution: authCacheService.getItem('cameraResolution'),
        correctOrientation: authCacheService.getItem('correctOrientation'),
        vidQuality: authCacheService.getItem('vidQuality'),
        copyToGallery: authCacheService.getItem('copyToGallery'),
        useAudioN10ns: authCacheService.getItem('useAudioN10ns'),
        audioN10nVolume: authCacheService.getItem('audioN10nVolume'),
        // Fetch this one if we have migrations to do.
        // previousAppVersion: authCacheService.getItem('previousAppVersion'),
      })
    )
    .then(data => {
      autoRetryUploads = data.autoRetryUploads;
      enablePushNotifications = data.enablePushNotifications;
      allowCameraEditing = data.allowCameraEditing;
      jpgQuality = data.jpgQuality;
      cameraResolution = data.cameraResolution;
      correctOrientation = data.correctOrientation;
      vidQuality = data.vidQuality;
      copyToGallery = data.copyToGallery;
      useAudioN10ns = data.useAudioN10ns;
      audioN10nVolume = data.audioN10nVolume;
      // const previousAppVersion = data.previousAppVersion;

      // Initialize to defaults if not found in the cache
      if (isNil(autoRetryUploads)) autoRetryUploads = false;
      if (isNil(enablePushNotifications)) enablePushNotifications = true;
      if (isNil(allowCameraEditing)) allowCameraEditing = false;
      if (isNil(jpgQuality)) jpgQuality = 60;
      if (isNil(cameraResolution)) cameraResolution = 1600;
      if (isNil(correctOrientation)) correctOrientation = true; // ionic.Platform.isIOS() || ionic.Platform.isIPad();
      if (isNil(vidQuality)) vidQuality = 1;
      if (isNil(copyToGallery)) copyToGallery = true;
      if (isNil(useAudioN10ns)) useAudioN10ns = true;
      if (isNil(audioN10nVolume)) audioN10nVolume = 100;

      // Convert types if needed
      if (isString(jpgQuality)) jpgQuality = parseFloat(jpgQuality);
      if (isString(audioN10nVolume)) audioN10nVolume = parseFloat(audioN10nVolume);

      // TODO: should probably dispatch an action here so other systems can respond as well
      // Apply future settings changes between versions here
      // Update to current version after migrations are complete
      authCacheService.setItem('previousAppVersion', CLIENT_VERSION);
    })
    .then(() => {
      // Notify services of initial values
      audioN10nVolume$.next(audioN10nVolume);
    });

  Object.defineProperties(this, {
    autoRetryUploads: {
      enumerable: true,
      configurable: false,
      get: function () {
        return autoRetryUploads;
      },
      set: function (nv) {
        autoRetryUploads = nv;
        authCacheService.setItem('autoRetryUploads', nv);
      },
    },
    enablePushNotifications: {
      enumerable: true,
      configurable: false,
      get: function () {
        return enablePushNotifications;
      },
      set: function (nv) {
        enablePushNotifications = nv;
        authCacheService.setItem('enablePushNotifications', nv);
      },
    },
    allowCameraEditing: {
      enumerable: true,
      configurable: false,
      get: function () {
        return allowCameraEditing;
      },
      set: function (nv) {
        allowCameraEditing = nv;
        authCacheService.setItem('allowCameraEditing', nv);
      },
    },
    jpgQuality: {
      enumerable: true,
      configurable: false,
      get: function () {
        return jpgQuality;
      },
      set: function (nv) {
        jpgQuality = isString(nv) ? parseFloat(nv) : nv;
        authCacheService.setItem('jpgQuality', jpgQuality);
      },
    },
    cameraResolution: {
      enumerable: true,
      configurable: false,
      get: function () {
        return cameraResolution;
      },
      set: function (nv) {
        cameraResolution = nv;
        authCacheService.setItem('cameraResolution', nv);
      },
    },
    cameraResolutions: {
      enumerable: true,
      configurable: false,
      value: cameraResolutions,
    },
    correctOrientation: {
      enumerable: true,
      configurable: false,
      get: function () {
        return correctOrientation;
      },
      set: function (nv) {
        correctOrientation = nv;
        authCacheService.setItem('correctOrientation', nv);
      },
    },
    vidQuality: {
      enumerable: true,
      configurable: false,
      get: function () {
        return vidQuality;
      },
      set: function (nv) {
        vidQuality = nv;
        authCacheService.setItem('vidQuality', nv);
      },
    },
    vidQualities: {
      enumerable: true,
      configurable: false,
      value: vidQualities,
    },
    copyToGallery: {
      enumerable: true,
      configurable: false,
      get: function () {
        return copyToGallery;
      },
      set: function (nv) {
        copyToGallery = nv;
        authCacheService.setItem('copyToGallery', nv);
      },
    },
    useAudioN10ns: {
      enumerable: true,
      configurable: false,
      get: function () {
        return useAudioN10ns;
      },
      set: function (nv) {
        useAudioN10ns = nv;
        authCacheService.setItem('useAudioN10ns', nv);
      },
    },
    audioN10nVolume: {
      enumerable: true,
      configurable: false,
      get: function () {
        return audioN10nVolume;
      },
      set: function (nv) {
        const isZero = nv === '0' || (isNumber(nv) && nv < 0.001);
        nv = isString(nv) ? parseFloat(nv) : nv;

        if (!useAudioN10ns && isNumber(audioN10nVolume) && nv > audioN10nVolume) {
          // If user is increasing volume but notifications are disabled, enable notifications.
          this.useAudioN10ns = true;
        } else if (useAudioN10ns && isZero) {
          // If the user has set volume to 0, disable notifications.
          this.useAudioN10ns = false;
        }

        audioN10nVolume = nv;
        authCacheService.setItem('audioN10nVolume', nv);
        audioN10nVolume$.next(nv);
      },
    },
  });

  this.jpgQualityOptions = {
    floor: 0,
    ceil: 100,
  };

  this.audioN10nVolumeOptions = {
    floor: 0,
    ceil: 100,
  };

  this.getCameraSettings = getCameraSettings;

  function getCameraSettings() {
    const rootState = $ngRedux.getState();
    const project = getProject(rootState);
    const organization = getOrganization(rootState);

    const data = {};
    // Check each source and only override if the previous version did not set the value
    if (get(project, 'overrideCameraSettings'))
      overrideIfNotDefined(data, project);

    if (get(organization, 'overrideCameraSettings'))
      overrideIfNotDefined(data, organization);

    overrideIfNotDefined(data, this);

    return data;
  }

  function overrideIfNotDefined(data, source) {
    if (!isNumber(data.cameraResolution) && isNumber(source.cameraResolution))
      data.cameraResolution = source.cameraResolution;

    if (!isNumber(data.jpgQuality) && isNumber(source.jpgQuality))
      data.jpgQuality = source.jpgQuality;

    if (!isNumber(data.vidQuality) && isNumber(source.vidQuality))
      data.vidQuality = source.vidQuality;
  }

  // Migrate the old cache to the new.
  function migrateExistingCache(cache, authCacheService) {
    try {
      const promises = [];

      const previousAppVersion = cache.get('previousAppVersion');
      const enablePushNotifications = cache.get('enablePushNotifications');
      const allowCameraEditing = cache.get('allowCameraEditing');
      let jpgQuality = cache.get('jpgQuality');
      let cameraResolution = cache.get('cameraResolution');
      let correctOrientation = cache.get('correctOrientation');
      let vidQuality = cache.get('vidQuality');

      // Migrate from < 1.0.7. Apply new default settings
      // TODO: should probably dispatch an action so other systems can respond as well
      if (!isNil(previousAppVersion)) {
        if (!isNil(jpgQuality)) {
          jpgQuality = 60;
        }
        if (!isNil(cameraResolution)) {
          cameraResolution = 1600;
        }
        if (!isNil(correctOrientation)) {
          correctOrientation = true;
        }
        if (!isNil(vidQuality)) {
          vidQuality = 1;
        }
      }

      if (!isNil(previousAppVersion)) {
        authCacheService.setItem('previousAppVersion', previousAppVersion);
      }
      if (!isNil(enablePushNotifications)) {
        authCacheService.setItem('enablePushNotifications', enablePushNotifications);
      }
      if (!isNil(allowCameraEditing)) {
        authCacheService.setItem('allowCameraEditing', allowCameraEditing);
      }
      if (!isNil(jpgQuality)) {
        if (isString(jpgQuality)) jpgQuality = parseFloat(jpgQuality);
        authCacheService.setItem('jpgQuality', jpgQuality);
      }
      if (!isNil(cameraResolution)) {
        authCacheService.setItem('cameraResolution', cameraResolution);
      }
      if (!isNil(correctOrientation)) {
        authCacheService.setItem('correctOrientation', correctOrientation);
      }
      if (!isNil(vidQuality)) {
        authCacheService.setItem('vidQuality', vidQuality);
      }

      cache.remove('previousAppVersion');
      cache.remove('enablePushNotifications');
      cache.remove('allowCameraEditing');
      cache.remove('jpgQuality');
      cache.remove('cameraResolution');
      cache.remove('correctOrientation');
      cache.remove('vidQuality');

      return Promise.all(promises);
    } catch (e) {
      try {
        cache.remove('previousAppVersion');
        cache.remove('enablePushNotifications');
        cache.remove('allowCameraEditing');
        cache.remove('jpgQuality');
        cache.remove('cameraResolution');
        cache.remove('correctOrientation');
        cache.remove('vidQuality');
      } catch (e) {
        return Promise.resolve(null);
      }
    }
  }
}

/**
 * @typedef {Object} AppSettings
 * @property {Boolean} autoRetryUploads
 * @property {Boolean} enablePushNotifications
 * @property {Boolean} allowCameraEditing
 * @property {Number} jpgQuality
 * @property {Number} cameraResolution
 * @property {Boolean} correctOrientation
 * @property {Number} vidQuality
 * @property {Boolean} useAudioN10ns
 * @property {Number} audioN10nVolume
 */



// WEBPACK FOOTER //
// ./src/js/settings/services/AppSettingsService.js